import React, { useCallback, useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { tenantAdminAPI } from '../../services/api';
import UniversityAiWorkloadSettings from './UniversityAiWorkloadSettings';
import {
  GEMINI_REASONING_EFFORT_OPTIONS,
  effectiveGeminiReasoningEffort,
  geminiReasoningOptionsForModel,
} from './global-admin/config';

const normalizeReasoningEffort = (value) => {
  const normalized = (value || '').toString().trim().toLowerCase();
  return GEMINI_REASONING_EFFORT_OPTIONS.some((option) => option.value === normalized) ? normalized : 'off';
};

const mapNumber = (value, fallback = '') => {
  if (value === null || value === undefined || value === '') return fallback;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? String(numeric) : String(value);
};

const optionalNumber = (value) => {
  if (value === null || value === undefined || value === '') return undefined;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : undefined;
};

const optionalTokenLimit = (value) => {
  const numeric = optionalNumber(value);
  return numeric === undefined ? null : Math.max(1, Math.floor(numeric));
};

const buildForm = (config = {}) => {
  const realtime = config.realtime || {};
  const marking = config.marking || {};
  const transcription = config.transcription || {};
  const compression = realtime.contextWindowCompression || {};
  const byok = config.funding?.mode === 'tenant_byok';
  return {
    realtime: {
      apiFamily: realtime.apiFamily || 'realtime',
      live: realtime.live || {},
      provider: realtime.provider || 'openai',
      parentApiKey: '',
      realtimeModel: realtime.realtimeModel || config.allowedRealtimeModels?.[0] || '',
      voice: realtime.voice || config.voices?.[0] || '',
      proactiveAudio: Boolean(realtime.proactiveAudio),
      affectiveDialog: Boolean(realtime.affectiveDialog),
      allowInterruptions: Boolean(realtime.allowInterruptions),
      toolCallingEnabled: realtime.toolCallingEnabled !== false,
      reasoningEffort: normalizeReasoningEffort(realtime.reasoningEffort || (realtime.thinkingEnabled ? realtime.thinkingLevel : 'off')),
      contextWindowCompressionEnabled: compression.enabled !== false,
      contextWindowCompressionTriggerTokens: mapNumber(compression.triggerTokens),
      contextWindowCompressionTargetTokens: mapNumber(compression.targetTokens),
      azureEndpoint: realtime.azure?.endpoint || '',
      azureApiKey: '',
      useGlobalCredentials: !byok,
    },
    marking: {
      provider: marking.provider || realtime.provider || 'openai',
      parentApiKey: '',
      markingModel: marking.markingModel || config.allowedMarkingModels?.[0] || '',
      azureEndpoint: marking.azure?.endpoint || '',
      azureDeploymentId: marking.azure?.deploymentId || '',
      azureApiVersion: marking.azure?.apiVersion || '',
      azureResourceName: marking.azure?.resourceName || '',
      azureApiKey: '',
      azureUseActiveDirectory: Boolean(marking.azure?.useActiveDirectory),
      useGlobalCredentials: !byok,
      parameters: {
        temperature: mapNumber(marking.parameters?.temperature, '0'),
        maxOutputTokens: mapNumber(marking.parameters?.maxOutputTokens),
        topP: mapNumber(marking.parameters?.topP),
        frequencyPenalty: mapNumber(marking.parameters?.frequencyPenalty),
        presencePenalty: mapNumber(marking.parameters?.presencePenalty),
        reasoningEffort: marking.parameters?.reasoningEffort || '',
        verbosity: marking.parameters?.verbosity || 'medium',
      },
    },
    transcription: {
      enabled: Boolean(transcription.enabled),
      provider: transcription.provider || 'openai',
      parentApiKey: '',
      model: transcription.model || 'gpt-4o-transcribe',
      azureEndpoint: transcription.azure?.endpoint || '',
      azureDeploymentId: transcription.azure?.deploymentId || '',
      azureApiVersion: transcription.azure?.apiVersion || '',
      azureResourceName: transcription.azure?.resourceName || '',
      azureApiKey: '',
      azureUseActiveDirectory: Boolean(transcription.azure?.useActiveDirectory),
      useGlobalCredentials: !byok,
    },
  };
};

const inheritedWorkloads = (config = {}) => [
  ['Realtime consultation', config.realtime?.provider, config.realtime?.realtimeModel],
  ['Case marking', config.marking?.provider, config.marking?.markingModel],
  ['Input transcription', config.transcription?.enabled === false ? 'Disabled' : config.transcription?.provider, config.transcription?.model],
];

function TenantAiSettingsPanel() {
  const [config, setConfig] = useState(null);
  const [form, setForm] = useState(() => buildForm());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState(null);

  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const response = await tenantAdminAPI.getAiConfiguration();
      const nextConfig = response.data || {};
      setConfig(nextConfig);
      setForm(buildForm(nextConfig));
      setAlert(null);
    } catch (error) {
      setAlert({ severity: 'error', message: error.response?.data?.message || 'Unable to load AI settings.' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const changeField = (area, field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setForm((current) => ({
      ...current,
      [area]: {
        ...current[area],
        [field]: value,
        // A typed secret belongs to the provider that was visible when it was
        // entered. Never carry it into a different provider's password field.
        ...(area === 'realtime' && field === 'provider' ? { parentApiKey: '' } : {}),
        ...(area === 'realtime' && field === 'provider' && value === 'gemini'
          && !String(current.realtime.realtimeModel || '').replace(/^models\//i, '').startsWith('gemini-')
          ? { realtimeModel: 'gemini-3.8-live' } : {}),
      },
    }));
  };

  const changeMarkingParameter = (field) => (event) => {
    setForm((current) => ({
      ...current,
      marking: {
        ...current.marking,
        parameters: { ...current.marking.parameters, [field]: event.target.value },
      },
    }));
  };

  const saveConfig = async () => {
    if (config?.funding?.mode !== 'tenant_byok' || config?.editableByTenant === false) return;
    const { realtime, marking, transcription } = form;
    if (!realtime.realtimeModel.trim() || !marking.markingModel.trim()) {
      setAlert({ severity: 'warning', message: 'Realtime and marking model names are required.' });
      return;
    }
    if (transcription.enabled && !transcription.model.trim()) {
      setAlert({ severity: 'warning', message: 'A transcription model is required while transcription is enabled.' });
      return;
    }
    const realtimeCredentialReady = realtime.provider === 'azure'
      ? (realtime.azureApiKey.trim() || config.realtime?.azureKeyConfigured)
      : (realtime.parentApiKey.trim()
        || (realtime.provider === 'gemini' ? config.realtime?.geminiKeyConfigured : config.realtime?.openaiKeyConfigured));
    const markingCredentialReady = marking.provider === 'azure'
      ? (marking.azureUseActiveDirectory || marking.azureApiKey.trim() || config.marking?.azureKeyConfigured)
      : (marking.parentApiKey.trim() || config.marking?.openaiKeyConfigured);
    const transcriptionCredentialReady = !transcription.enabled || (transcription.provider === 'azure'
      ? (transcription.azureUseActiveDirectory || transcription.azureApiKey.trim() || config.transcription?.azureKeyConfigured)
      : (transcription.parentApiKey.trim() || config.transcription?.openaiKeyConfigured));
    if (!realtimeCredentialReady || !markingCredentialReady || !transcriptionCredentialReady) {
      setAlert({ severity: 'warning', message: 'Add credentials for every enabled workload before saving.' });
      return;
    }
    if (realtime.provider === 'azure' && !realtime.azureEndpoint.trim()) {
      setAlert({ severity: 'warning', message: 'Add the Azure OpenAI resource endpoint before saving.' });
      return;
    }
    setSaving(true);
    try {
      await tenantAdminAPI.updateAiConfiguration({
        realtime: {
          apiFamily: realtime.apiFamily,
          live: realtime.live,
          provider: realtime.provider,
          parentApiKey: realtime.parentApiKey,
          realtimeModel: realtime.realtimeModel.trim(),
          voice: realtime.voice,
          proactiveAudio: Boolean(realtime.proactiveAudio),
          affectiveDialog: Boolean(realtime.affectiveDialog),
          allowInterruptions: Boolean(realtime.allowInterruptions),
          toolCallingEnabled: Boolean(realtime.toolCallingEnabled),
          reasoningEffort: effectiveGeminiReasoningEffort(realtime.realtimeModel, normalizeReasoningEffort(realtime.reasoningEffort)),
          contextWindowCompression: {
            enabled: realtime.contextWindowCompressionEnabled !== false,
            triggerTokens: optionalTokenLimit(realtime.contextWindowCompressionTriggerTokens),
            targetTokens: optionalTokenLimit(realtime.contextWindowCompressionTargetTokens),
          },
          azureEndpoint: realtime.azureEndpoint,
          azureApiKey: realtime.azureApiKey,
          useGlobalCredentials: false,
        },
        marking: {
          provider: marking.provider,
          parentApiKey: marking.parentApiKey,
          markingModel: marking.markingModel.trim(),
          azureEndpoint: marking.azureEndpoint,
          azureDeploymentId: marking.azureDeploymentId,
          azureApiVersion: marking.azureApiVersion,
          azureResourceName: marking.azureResourceName,
          azureApiKey: marking.azureApiKey,
          azureUseActiveDirectory: Boolean(marking.azureUseActiveDirectory),
          useGlobalCredentials: false,
          parameters: {
            temperature: optionalNumber(marking.parameters.temperature),
            maxOutputTokens: optionalNumber(marking.parameters.maxOutputTokens),
            topP: optionalNumber(marking.parameters.topP),
            frequencyPenalty: optionalNumber(marking.parameters.frequencyPenalty),
            presencePenalty: optionalNumber(marking.parameters.presencePenalty),
            reasoningEffort: marking.parameters.reasoningEffort?.trim() || '',
            verbosity: marking.parameters.verbosity || 'medium',
          },
        },
        transcription: {
          enabled: Boolean(transcription.enabled),
          provider: transcription.provider,
          parentApiKey: transcription.parentApiKey,
          model: transcription.model.trim(),
          azureEndpoint: transcription.azureEndpoint,
          azureDeploymentId: transcription.azureDeploymentId,
          azureApiVersion: transcription.azureApiVersion,
          azureResourceName: transcription.azureResourceName,
          azureApiKey: transcription.azureApiKey,
          azureUseActiveDirectory: Boolean(transcription.azureUseActiveDirectory),
          useGlobalCredentials: false,
        },
      });
      await loadConfig();
      setAlert({ severity: 'success', message: 'AI configuration updated.' });
    } catch (error) {
      setAlert({ severity: 'error', message: error.response?.data?.message || 'Unable to save AI settings.' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box sx={{ py: 7, display: 'grid', placeItems: 'center' }}><CircularProgress /></Box>;

  if (!config) return <Alert severity="error">{alert?.message || 'AI settings are unavailable.'}</Alert>;

  const byok = config.funding?.mode === 'tenant_byok';

  return (
    <Stack spacing={2.5}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1.5}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>AI services</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Credential ownership is selected by the Virtual Medic global administrator.
          </Typography>
        </Box>
        <Chip
          label={byok ? 'University API keys · No credit charge' : 'Platform managed · Uses credits'}
          color={byok ? 'success' : 'primary'}
          sx={{ alignSelf: { xs: 'flex-start', sm: 'center' }, fontWeight: 800 }}
        />
      </Stack>

      {alert && <Alert severity={alert.severity} onClose={() => setAlert(null)}>{alert.message}</Alert>}

      {!byok ? (
        <Stack spacing={2}>
          <Alert severity="info">
            This university uses platform-managed AI. Credentials, providers, models, and parameters automatically follow the global configuration, and usage is charged to the university's Virtual Medic credit wallet.
          </Alert>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
            {inheritedWorkloads(config).map(([label, provider, model]) => (
              <Paper key={label} variant="outlined" sx={{ p: 2, flex: 1 }}>
                <Typography variant="caption" color="text.secondary">{label}</Typography>
                <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 800, textTransform: 'capitalize' }}>{provider || 'Global default'}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>{model || 'Global model'}</Typography>
              </Paper>
            ))}
          </Stack>
          <Typography variant="caption" color="text.secondary">
            Funding mode can only be changed from the global admin panel. No university API keys are stored or requested in this mode.
          </Typography>
        </Stack>
      ) : (
        <Stack spacing={2}>
          <Alert severity="warning">
            Add credentials for every enabled workload. Keys are isolated to this university and platform credentials are never used as a fallback.
          </Alert>
          <UniversityAiWorkloadSettings
            config={config}
            realtime={form.realtime}
            marking={form.marking}
            transcription={form.transcription}
            voices={config.voices || []}
            reasoningEffortOptions={geminiReasoningOptionsForModel(form.realtime.realtimeModel)}
            normalizeReasoningEffort={normalizeReasoningEffort}
            onRealtimeChange={(field) => changeField('realtime', field)}
            onMarkingChange={(field) => changeField('marking', field)}
            onTranscriptionChange={(field) => changeField('transcription', field)}
            onMarkingParameterChange={changeMarkingParameter}
            verbosityDefault="medium"
          />
          <Stack direction="row" spacing={1.5}>
            <Button variant="contained" onClick={saveConfig} disabled={saving || config.editableByTenant === false}>
              {saving ? 'Saving…' : 'Save AI settings'}
            </Button>
            <Button variant="outlined" onClick={loadConfig} disabled={saving}>Reset</Button>
          </Stack>
        </Stack>
      )}
    </Stack>
  );
}

export default TenantAiSettingsPanel;
